/**
 * Páginas do PDF "Por que consórcio" (estudo.ts → HTML de cada página A4).
 *
 * Cada página é um bloco de 794 × 1123 px (A4 a 96 dpi) fotografado sozinho,
 * então nada é cortado no meio. Visual de apresentação, não de relatório: a
 * capa escura com o logo dourado, um número grande por ideia e um gráfico por
 * página — a referência foram as telas do O Cenário que o sócio do Rick
 * gostou, nas cores da North (marinho e o dourado do logo).
 *
 * Modo 'tela' (link do estudo): as mesmas páginas viram seções de largura
 * inteira, sem altura fixa, como um site. O conteúdo tem a largura do PDF
 * (CONTEUDO_TELA) e cresce ou encolhe com `zoom: var(--estudo-zoom)`, que a
 * tela calcula pela largura disponível (EstudoTela.tsx).
 *
 * Modo 'celular' (o link aberto no telefone): o mesmo conteúdo em 360 px, com
 * os cartões um embaixo do outro e sem os parágrafos, as notas e as tabelas
 * largas — só o título, os números e o gráfico de cada seção (Rick, 17/10/2026).
 */
import { formatBRL } from '@/lib/format';
import { NOME_SEGMENTO } from './comparativoPdf';
import type { Estudo } from './estudo';
import { CENARIOS } from './estudo';
import { graficoBarras, graficoLinhas, reaisCurto } from './graficosSvg';
import { tabelaAnoAno } from './parcelasAnoAno';
import {
  PRESO_AO_PE,
  COR,
  LINHA_CARTOES,
  caixaGrafico,
  cartaoNumero,
  duasEstrategias,
  escapar,
  larguraGrafico,
  linhaTabela,
  pagina,
  pct,
  rent,
  tituloSecao,
  type ModoPaginas,
} from './estudoMoldura';
import { NOME_CENARIO } from './resumoCliente';
import type { UserInfo } from './proposal-utils';
import { paginaEmpate } from './estudoPaginaEmpate';

export { COR, CONTEUDO_CELULAR, CONTEUDO_TELA, FONTE_PDF, type ModoPaginas } from './estudoMoldura';

// ── 1. capa ──────────────────────────────────────────────────────────────

const capa = (e: Estudo, userInfo: UserInfo, origin: string, modo: ModoPaginas) => {
  const curto = modo === 'celular';
  const f = e.comparativo.financiamento;
  // Rick, 18/10/2026: a capa mostra só o objetivo do plano. Mês e forma de
  // contemplação são premissas (o "9x … → depois" parecia promessa); ficam
  // nas páginas das parcelas.
  const custoTaxa = (e.entrada.credito * e.entrada.taxaAdminPct) / 100;
  const kpis = [
    { rotulo: 'Parcela', valor: formatBRL(e.entrada.parcelaPre), detalhe: 'Até a contemplação, sem seguro' },
    { rotulo: 'Custo da taxa de administração', valor: formatBRL(custoTaxa), detalhe: `${pct(e.entrada.taxaAdminPct)} do crédito, sem juros` },
    { rotulo: 'Total do plano', valor: formatBRL(e.entrada.credito + custoTaxa), detalhe: 'Crédito + taxa de administração' },
  ];
  const kpiHtml = kpis
    .map(
      (k) => `<div style="flex:1 1 200px;background:rgba(255,255,255,0.06);border:1px solid rgba(196,173,142,0.35);border-top:3px solid ${COR.ouro};border-radius:14px;padding:16px 16px 14px;">
        <div style="font-size:9.5px;font-weight:700;letter-spacing:0.14em;text-transform:uppercase;color:${COR.ouro};">${k.rotulo}</div>
        <div style="font-size:22px;font-weight:800;margin-top:10px;">${k.valor}</div>
        <div style="font-size:10px;margin-top:6px;color:rgba(255,255,255,0.65);line-height:1.4;">${k.detalhe}</div>
      </div>`,
    )
    .join('');
  const economia =
    f.economia > 0
      ? `<div style="margin-top:26px;display:flex;flex-wrap:wrap;align-items:flex-end;gap:8px 16px;">
          <div style="font-size:44px;font-weight:800;color:${COR.ouro};letter-spacing:-0.02em;line-height:1;">${formatBRL(f.economia)}</div>
          <div style="font-size:12px;color:rgba(255,255,255,0.75);line-height:1.45;padding-bottom:4px;">a menos do que os juros de um${curto ? ' ' : '<br/>'}financiamento ${f.financiamento.sistema} do mesmo valor</div>
        </div>`
      : '';
  const contato = `<div style="display:flex;flex-wrap:wrap;justify-content:space-between;align-items:center;gap:12px;">
      <div>
        <div style="font-size:9.5px;letter-spacing:0.14em;text-transform:uppercase;color:${COR.ouro};">Seu especialista</div>
        <div style="font-size:15px;font-weight:700;margin-top:6px;">${escapar(userInfo.name ?? 'North Business Team')}</div>
        <div style="font-size:10.5px;margin-top:4px;color:rgba(255,255,255,0.7);">${[userInfo.phone, userInfo.email].filter(Boolean).map(escapar).join(' · ')}</div>
      </div>
      <div style="background:#fff;border-radius:12px;padding:8px 14px;">
        <img src="${origin}/logos/servopa_parceiro.webp" style="height:30px;" />
      </div>
    </div>`;

  return `
    <div style="display:flex;flex-wrap:wrap;justify-content:space-between;align-items:center;gap:10px 16px;">
      <img src="${origin}/logos/logo_full.png" style="height:${curto ? 34 : 42}px;" />
      <div style="font-size:10px;letter-spacing:0.22em;text-transform:uppercase;color:${COR.ouro};">Apresentação de consórcio</div>
    </div>
    <div style="margin-top:${curto ? 44 : 90}px;">
      <div style="font-size:12px;letter-spacing:0.2em;text-transform:uppercase;color:rgba(255,255,255,0.6);">
        ${e.cliente ? `Preparado para ${escapar(e.cliente)}` : 'Apresentação personalizada'} · ${NOME_SEGMENTO[e.segmento]}
      </div>
      <div style="font-size:${curto ? 40 : 50}px;font-weight:800;line-height:1.05;margin-top:14px;letter-spacing:-0.02em;">
        Seu plano de<br/><span style="color:${COR.ouro};">${formatBRL(e.entrada.credito)}</span>
      </div>
      <div style="font-size:13px;color:rgba(255,255,255,0.7);margin-top:14px;">
        ${e.entrada.prazo} meses · taxa de administração de ${pct(e.entrada.taxaAdminPct)} · sem juros
      </div>
      ${economia}
    </div>
    <div style="${LINHA_CARTOES}margin-top:40px;">${kpiHtml}</div>
    <div style="${PRESO_AO_PE}">${contato}</div>`;
};

// ── 2. financiamento ────────────────────────────────────────────────────

const paginaFinanciamento = (e: Estudo, num: string, modo: ModoPaginas) => {
  const curto = modo === 'celular';
  const f = e.comparativo.financiamento;
  const ganha = f.economia > 0;
  const cartaoSistema = (nome: string, total: number, parcela: string, custo: number, rotuloCusto: string, cor: string, escuro = false) =>
    cartaoNumero({
      rotulo: nome,
      valor: formatBRL(total),
      detalhe: `Total pago · ${parcela}<br/><span style="font-weight:700;color:${escuro ? COR.ouro : cor};">${rotuloCusto}: ${formatBRL(custo)}</span>`,
      escuro,
      destaque: escuro ? COR.ouro : cor,
    });
  const svg = graficoLinhas({
    largura: larguraGrafico(modo),
    altura: curto ? 240 : 330,
    series: [
      { nome: 'Price', cor: COR.vermelho, valores: e.custo.price },
      { nome: 'SAC', cor: COR.azulClaro, valores: e.custo.sac },
      { nome: 'Consórcio', cor: COR.ouro, valores: e.custo.consorcio, area: true, espessura: 3.5 },
    ],
    rotuloX: (i) => `Mês ${i + 1}`,
    valorNaPonta: true,
  });
  const economias = [
    ['SAC', f.sac.juros - f.consorcioCusto],
    ['Price', f.price.juros - f.consorcioCusto],
  ] as const;
  return `
    ${tituloSecao(
      `${num} · O custo do dinheiro`,
      ganha ? 'Sem juros, o consórcio custa menos que financiar' : 'Consórcio × financiamento',
      `Mesmo crédito (${formatBRL(e.entrada.credito)}) e mesmo prazo (${e.entrada.prazo} meses). Financiamento com juros de ${pct(e.taxas.jurosFinanciamentoAa, 2)} ao ano; consórcio com taxa de administração de ${pct(e.entrada.taxaAdminPct)}.`,
      modo,
    )}
    <div style="${LINHA_CARTOES}">
      ${cartaoSistema('Consórcio', f.consorcioTotal, `parcela ${formatBRL(e.entrada.parcelaPre)}`, f.consorcioCusto, 'Taxa', COR.ouro, true)}
      ${cartaoSistema('Financiamento SAC', f.sac.totalPago, `1ª parcela ${formatBRL(f.sac.parcelaInicial)}`, f.sac.juros, 'Juros', COR.azulClaro)}
      ${cartaoSistema('Financiamento Price', f.price.totalPago, `parcela ${formatBRL(f.price.parcelaInicial)}`, f.price.juros, 'Juros', COR.vermelho)}
    </div>
    ${caixaGrafico('Custo acumulado ao longo do plano', svg, [
      { nome: 'Consórcio', cor: COR.ouro },
      { nome: 'SAC', cor: COR.azulClaro },
      { nome: 'Price', cor: COR.vermelho },
    ])}
    <div style="${LINHA_CARTOES}margin-top:14px;">
      ${economias
        .map(([nome, v]) => {
          const pos = v >= 0;
          return `<div class="estudo-cartao" style="flex:1 1 200px;border-radius:14px;padding:14px 18px;background:${pos ? '#EAF5EE' : '#FFF6E5'};">
            <div style="font-size:10px;font-weight:700;letter-spacing:0.1em;text-transform:uppercase;color:${pos ? COR.verde : '#9A6B00'};">Frente ao ${nome}</div>
            <div style="font-size:22px;font-weight:800;margin-top:6px;color:${pos ? COR.verde : '#9A6B00'};">${pos ? '' : '+'}${formatBRL(Math.abs(v))}</div>
            <div style="font-size:10.5px;color:${COR.suave};margin-top:4px;">${pos ? 'de economia no consórcio' : 'de custo a mais no consórcio neste prazo'}</div>
          </div>`;
        })
        .join('')}
    </div>
    ${curto ? '' : `<div style="font-size:10.5px;color:${COR.suave};margin-top:14px;line-height:1.5;">
      No financiamento o bem é liberado na assinatura; no consórcio, na contemplação. Valores sem seguros, TR e tarifas.
    </div>`}`;
};

// ── 3. venda da carta ───────────────────────────────────────────────────

/** Sensibilidade: o mesmo cálculo em outros meses de contemplação. */
const tabelaVenda = (e: Estudo) => {
  const th = `padding:6px 8px;font-size:9.5px;font-weight:700;letter-spacing:0.06em;text-transform:uppercase;color:${COR.suave};text-align:right;`;
  const linhas = e.comparativo.tabela
    .map((l) => {
      const dif = l.recebidoLiquido - l.cdbLiquido;
      const destaque = l.mes === e.comparativo.cenarioProposta.mes ? `background:${COR.ouroClaro};font-weight:700;` : '';
      const td = 'padding:5px 8px;text-align:right;border-bottom:1px solid #eef1f5;';
      return `<tr style="${destaque}">
        <td style="${td}text-align:left;">Mês ${l.mes}</td>
        <td style="${td}">${formatBRL(l.totalPago)}</td>
        <td style="${td}">${formatBRL(l.recebidoLiquido)}</td>
        <td style="${td}">${formatBRL(l.cdbLiquido)}</td>
        <td style="${td}color:${dif >= 0 ? COR.verde : '#9A6B00'};font-weight:700;">${dif >= 0 ? '+' : '−'}${formatBRL(Math.abs(dif))}</td>
      </tr>`;
    })
    .join('');
  return `<table style="width:100%;border-collapse:collapse;margin-top:16px;font-size:10.5px;">
    <thead><tr>
      <th style="${th}text-align:left;">Contemplação</th><th style="${th}">Pago</th><th style="${th}">Venda da carta</th><th style="${th}">CDB</th><th style="${th}">Diferença</th>
    </tr></thead><tbody>${linhas}</tbody></table>`;
};

const paginaVenda = (e: Estudo, num: string, modo: ModoPaginas) => {
  const curto = modo === 'celular';
  const v = e.comparativo.cenarioProposta;
  const m = v.mes;
  const horizonte = Math.min(e.entrada.prazo, Math.max(m * 2, Math.min(e.entrada.prazo, 60)));
  const corte = <T,>(a: T[]) => a.slice(0, horizonte);
  const svg = graficoLinhas({
    largura: larguraGrafico(modo),
    altura: curto ? 220 : 270,
    series: [
      { nome: 'Pago', cor: '#9AA6B5', valores: corte(e.venda.pago), tracejado: true, espessura: 2 },
      { nome: 'CDB', cor: COR.azulClaro, valores: corte(e.venda.cdb) },
      { nome: 'Venda da carta', cor: COR.ouro, valores: corte(e.venda.carta), area: true, espessura: 3.5 },
    ],
    rotuloX: (i) => `Mês ${i + 1}`,
    marcador: { indice: m - 1, rotulo: `Mês ${m}` },
  });
  const diferenca = v.recebidoLiquido - v.cdbLiquido;
  return `
    ${tituloSecao(
      `${num} · Consórcio como investimento`,
      `Contemplar no mês ${m} e vender a carta`,
      `As mesmas parcelas, nos mesmos meses: vender a carta contemplada com ${pct(e.taxas.agioPct)} de ágio ou aplicar o dinheiro num CDB a 100% do CDI (${pct(e.taxas.cdiAa, 2)} ao ano), com imposto de renda nos dois casos.`,
      modo,
    )}
    <div style="${LINHA_CARTOES}">
      ${cartaoNumero({ rotulo: 'Pago até o mês ' + m, valor: formatBRL(v.totalPago), detalhe: 'Parcelas com reajuste anual' })}
      ${cartaoNumero({
        rotulo: 'Recebe vendendo a carta',
        valor: formatBRL(v.recebidoLiquido),
        detalhe: `Lucro de ${formatBRL(v.lucroCarta)} · ${rent(v.rentabilidadeCartaAa)}`,
        escuro: true,
        destaque: COR.ouro,
      })}
      ${cartaoNumero({
        rotulo: 'Saldo no CDB',
        valor: formatBRL(v.cdbLiquido),
        detalhe: `Lucro de ${formatBRL(v.lucroCdb)} · ${rent(v.rentabilidadeCdbAa)}`,
        destaque: COR.azulClaro,
      })}
    </div>
    ${caixaGrafico('Venda da carta × CDB, mês a mês', svg, [
      { nome: 'Venda da carta', cor: COR.ouro },
      { nome: 'CDB', cor: COR.azulClaro },
      { nome: 'Total pago', cor: '#9AA6B5', tracejado: true },
    ])}
    <div style="${LINHA_CARTOES}margin-top:14px;">
      <div style="flex:1.3 1 200px;border-radius:14px;padding:16px 18px;background:${diferenca >= 0 ? '#EAF5EE' : '#FFF6E5'};">
        <div style="font-size:10px;font-weight:700;letter-spacing:0.1em;text-transform:uppercase;color:${diferenca >= 0 ? COR.verde : '#9A6B00'};">
          ${diferenca >= 0 ? 'A favor do consórcio' : 'A favor do CDB'}
        </div>
        <div style="font-size:26px;font-weight:800;margin-top:6px;color:${diferenca >= 0 ? COR.verde : '#9A6B00'};">${formatBRL(Math.abs(diferenca))}</div>
      </div>
      <div style="flex:1 1 200px;border-radius:14px;padding:16px 18px;background:${COR.ouroClaro};">
        <div style="font-size:10px;font-weight:700;letter-spacing:0.1em;text-transform:uppercase;color:#8A6F45;">Ágio para empatar</div>
        <div style="font-size:26px;font-weight:800;margin-top:6px;color:${COR.marinho};">${pct(v.agioEmpatePct)}</div>
        <div style="font-size:10.5px;color:${COR.suave};margin-top:2px;">do crédito; acima disso, a carta rende mais</div>
      </div>
    </div>
    ${curto ? '' : `${tabelaVenda(e)}
    <div style="font-size:10.5px;color:${COR.suave};margin-top:12px;line-height:1.5;">
      ${e.comparativo.ultimoMesQueGanha ? `Com ${pct(e.taxas.agioPct)} de ágio, vender a carta rende mais que o CDB contemplando até o mês ${e.comparativo.ultimoMesQueGanha}. ` : ''}O ágio é negociado caso a caso e não é garantido; IR de 15% sobre o ganho da carta e tabela regressiva no CDB.
    </div>`}`;
};

// ── 4. patrimônio ───────────────────────────────────────────────────────

const paginaPatrimonio = (e: Estudo, num: string, modo: ModoPaginas) => {
  const curto = modo === 'celular';
  const p = e.patrimonio!;
  const r = e.resumo!;
  const a = p.aplicado;
  const im = p.imovel;
  const m = r.mesContemplacao;
  const rotulo = (i: number) => `Mês ${m + i + 1}`;
  const blocoImovel = im
    ? `<div style="${LINHA_CARTOES}">
        ${cartaoNumero({ rotulo: 'Patrimônio no fim do plano', valor: formatBRL(im.patrimonio), detalhe: `Imóvel ${reaisCurto(im.valorFinalImovel)} + aluguéis ${reaisCurto(im.alugueisAcumulados)}`, escuro: true, destaque: COR.ouro })}
        ${cartaoNumero({ rotulo: 'O aluguel paga', valor: pct(im.coberturaPct, 0), detalhe: `da parcela: ${formatBRL(im.primeiroAluguel)} de aluguel para ${formatBRL(im.primeiraParcela)} de parcela` })}
        ${cartaoNumero({ rotulo: 'Resultado', valor: formatBRL(im.resultado), detalhe: 'Patrimônio menos tudo o que foi pago', destaque: im.resultado >= 0 ? COR.verde : COR.vermelho })}
      </div>
      ${caixaGrafico(
        'Imóvel e aluguel: o patrimônio depois da contemplação',
        graficoLinhas({
          largura: larguraGrafico(modo),
          altura: curto ? 180 : 200,
          series: [
            { nome: 'Aluguéis', cor: COR.azulClaro, valores: im.serieAlugueis },
            { nome: 'Imóvel', cor: '#9AA6B5', valores: im.serieImovel, tracejado: true, espessura: 2 },
            { nome: 'Patrimônio', cor: COR.ouro, valores: im.serieImovel.map((v, i) => v + im.serieAlugueis[i]), area: true, espessura: 3.5 },
          ],
          rotuloX: rotulo,
          valorNaPonta: true,
        }),
        [
          { nome: 'Patrimônio', cor: COR.ouro },
          { nome: 'Imóvel', cor: '#9AA6B5', tracejado: true },
          { nome: 'Aluguéis', cor: COR.azulClaro },
        ],
      )}`
    : '';
  const blocoAplicado = `
    <div style="${LINHA_CARTOES}margin-top:${im ? 22 : 0}px;">
      ${cartaoNumero({ rotulo: 'Saldo da aplicação no fim', valor: formatBRL(a.saldoFinal), detalhe: `Depois de pagar ${a.meses} parcelas e o IR`, escuro: !im, destaque: COR.ouro })}
      ${cartaoNumero({ rotulo: 'Rendimento', valor: formatBRL(a.rendimentoBruto), detalhe: `IR de ${formatBRL(a.ir)}` })}
      ${cartaoNumero({ rotulo: 'Resultado', valor: formatBRL(a.resultado), detalhe: 'Saldo menos o aporte até a contemplação', destaque: a.resultado >= 0 ? COR.verde : COR.vermelho })}
    </div>
    ${caixaGrafico(
      'Crédito aplicado: o saldo enquanto paga as parcelas',
      graficoLinhas({
        largura: larguraGrafico(modo),
        altura: im || curto ? 170 : 300,
        series: [{ nome: 'Saldo', cor: COR.azul, valores: a.serie, area: true, espessura: 3 }],
        rotuloX: rotulo,
        valorNaPonta: true,
      }),
      [{ nome: 'Saldo da aplicação', cor: COR.azul }],
    )}`;
  return `
    ${tituloSecao(
      `${num} · Depois da contemplação`,
      im ? 'Imóvel para alugar ou crédito aplicado' : 'Parcelas pagas pelo crédito aplicado',
      `${escapar(NOME_CENARIO[r.cenario])}, contemplação no ${m}º mês e crédito líquido de ${formatBRL(p.credito)} (com reajuste). ${im ? 'Duas estratégias: comprar um imóvel para alugar, ou aplicar o crédito e pagar as parcelas com o rendimento.' : 'O crédito vai para uma aplicação a 100% do CDI e as parcelas restantes saem dela.'}`,
      modo,
    )}
    ${im && modo !== 'pdf' ? duasEstrategias(blocoImovel, blocoAplicado.replace('margin-top:22px;', '')) : blocoImovel + blocoAplicado}`;
};

// ── 5. parcelas ─────────────────────────────────────────────────────────

const paginaParcelas = (e: Estudo, num: string, modo: ModoPaginas) => {
  const curto = modo === 'celular';
  const r = e.resumo!;
  const series = e.parcelas!.comReajuste;
  const linhas = tabelaAnoAno(series, r.mesContemplacao);
  const escolhida = series[r.cenario];
  const anos = Math.ceil(escolhida.length / 12);
  const grupos = Array.from({ length: anos }, (_, a) => {
    const mes = a * 12 + 1;
    const valor = escolhida[mes - 1] ?? 0;
    const antes = mes <= r.mesContemplacao;
    return {
      rotulo: `${a + 1}º`,
      valores: [{ valor, cor: antes ? COR.azulClaro : COR.ouro, dica: `${a + 1}º ano · ${antes ? 'antes' : 'depois'} da contemplação · ${formatBRL(valor)}` }],
    };
  });
  const svg = graficoBarras({ largura: larguraGrafico(modo), altura: curto ? 180 : 210, grupos, valorNoTopo: anos <= (curto ? 7 : 14) });
  const maxLinhas = 20;
  const visiveis = linhas.length > maxLinhas ? linhas.filter((l, i) => l.posContemplacao || i % Math.ceil(linhas.length / maxLinhas) === 0) : linhas;
  const th = `padding:7px 8px;font-size:9.5px;font-weight:700;letter-spacing:0.06em;text-transform:uppercase;color:${COR.suave};text-align:right;`;
  const hoje = r.semReajuste;
  const corr = r.comReajuste;
  return `
    ${tituloSecao(
      `${num} · As parcelas`,
      'Ano a ano, em cada forma de contemplação',
      `Parcela do primeiro mês de cada ano, com reajuste de ${pct(e.taxas.reajusteAa, 2)} ao ano. Contemplação no ${r.mesContemplacao}º mês; a linha dourada marca a primeira parcela depois dela.`,
      modo,
    )}
    ${caixaGrafico(`${escapar(NOME_CENARIO[r.cenario])}: parcela por ano do plano`, svg, [
      { nome: 'Antes da contemplação', cor: COR.azulClaro },
      { nome: 'Depois', cor: COR.ouro },
    ])}
    ${curto ? '' : `<table style="width:100%;border-collapse:collapse;margin-top:14px;font-size:10.5px;">
      <thead><tr>
        <th style="${th}text-align:left;"></th>
        ${CENARIOS.map((c) => `<th style="${th}${c === r.cenario ? `color:${COR.marinho};` : ''}">${NOME_CENARIO[c]}</th>`).join('')}
      </tr></thead>
      <tbody>
        ${visiveis
          .map(
            (l) => `<tr style="${l.posContemplacao ? `background:${COR.ouroClaro};font-weight:700;` : ''}">
              <td style="padding:5px 8px;border-bottom:1px solid #eef1f5;">${l.rotulo}${l.posContemplacao ? ' · após contemplar' : ''}</td>
              ${CENARIOS.map(
                (c) => `<td style="padding:5px 8px;text-align:right;border-bottom:1px solid #eef1f5;${c === r.cenario ? 'font-weight:700;' : ''}">${l.valores[c] === null ? '—' : formatBRL(l.valores[c] as number)}</td>`,
              ).join('')}
            </tr>`,
          )
          .join('')}
      </tbody>
    </table>`}
    <div style="${LINHA_CARTOES}margin-top:18px;">
      <div style="flex:1 1 200px;background:${COR.fundo};border-radius:14px;padding:12px 16px;">
        <div style="font-size:10px;font-weight:700;letter-spacing:0.1em;text-transform:uppercase;color:${COR.suave};margin-bottom:4px;">Valores de hoje</div>
        ${linhaTabela('Total pago no plano', formatBRL(hoje.totalPago))}
        ${linhaTabela('Custo total', `${formatBRL(hoje.custo)} · ${pct(hoje.custoPctDoCredito)}`, true)}
      </div>
      <div style="flex:1 1 200px;background:${COR.fundo};border-radius:14px;padding:12px 16px;">
        <div style="font-size:10px;font-weight:700;letter-spacing:0.1em;text-transform:uppercase;color:${COR.suave};margin-bottom:4px;">Com reajuste de ${pct(e.taxas.reajusteAa, 2)} a.a.</div>
        ${linhaTabela('Total pago no plano', formatBRL(corr.totalPago))}
        ${linhaTabela('Crédito corrigido', formatBRL(corr.creditoLiquido), true)}
      </div>
    </div>`;
};

// ── 6. premissas ────────────────────────────────────────────────────────

const paginaPremissas = (e: Estudo, userInfo: UserInfo, num: string, modo: ModoPaginas) => {
  const curto = modo === 'celular';
  const o = e.opcoes;
  const itens: Array<[string, string, string]> = [
    ['Juros do financiamento', `${pct(e.taxas.jurosFinanciamentoAa, 2)} a.a.`, o.fontes.juros],
    ['CDI', `${pct(o.cdiAa, 2)} a.a.`, o.fontes.cdi],
    ['Reajuste anual do consórcio', `${pct(o.reajusteAa, 2)}`, o.fontes.reajuste],
    ['Ágio na venda da carta', pct(o.agioPct), o.fontes.agio],
    ...(e.segmento === 'imovel' && e.patrimonio
      ? ([
          ['Valorização do imóvel', `${pct(o.valorizacaoAa, 2)} a.a.`, o.fontes.valorizacao],
          ['Aluguel líquido', `${pct(o.aluguelAm, 2)} do valor ao mês`, o.fontes.aluguel],
        ] as Array<[string, string, string]>)
      : []),
  ];
  return `
    ${tituloSecao(`${num} · Premissas`, 'Índices considerados', 'Índices oficiais do Banco Central sempre que existem; os ajustados pelo especialista aparecem indicados.', modo)}
    <div style="background:${COR.fundo};border-radius:16px;padding:8px 20px;">
      ${itens
        .map(
          ([nome, valor, fonte]) => `<div style="display:flex;align-items:center;justify-content:space-between;padding:12px 0;border-bottom:1px solid #e3e8ef;">
            <div><div style="font-size:12.5px;font-weight:700;color:${COR.marinho};">${nome}</div>
            <div style="font-size:10.5px;color:${COR.suave};margin-top:3px;">${escapar(fonte)}</div></div>
            <div style="font-size:17px;font-weight:800;color:${COR.marinho};">${valor}</div>
          </div>`,
        )
        .join('')}
    </div>
    ${curto ? '' : `<div style="font-size:10.5px;color:${COR.suave};line-height:1.65;margin-top:18px;">
      <p style="margin:0 0 8px;"><b style="color:${COR.texto};">Consórcio.</b> Taxa de administração de ${pct(e.entrada.taxaAdminPct)} sobre o crédito, sem juros. Crédito e parcelas reajustados uma vez por ano. Custo total = tudo o que se paga no plano menos o crédito líquido recebido, incluindo seguro quando contratado.</p>
      <p style="margin:0 0 8px;"><b style="color:${COR.texto};">Financiamento.</b> Mesmo valor e prazo, SAC e Tabela Price, sem TR, seguros e tarifas.</p>
      <p style="margin:0 0 8px;"><b style="color:${COR.texto};">Aplicações.</b> CDB a 100% do CDI com IR regressivo (22,5% a 15%); crédito aplicado com IR de 15% sobre o rendimento. Taxas mantidas constantes no período.</p>
      <p style="margin:0;">Simulação ilustrativa, sem valor contratual. A contemplação por sorteio ou lance não é garantida; regras e percentuais seguem o contrato da administradora.</p>
    </div>`}
    <div style="${PRESO_AO_PE}border-radius:18px;padding:22px 26px;background:linear-gradient(135deg, ${COR.marinho}, ${COR.noite});color:#fff;display:flex;flex-wrap:wrap;gap:12px;justify-content:space-between;align-items:center;">
      <div>
        <div style="font-size:10px;letter-spacing:0.16em;text-transform:uppercase;color:${COR.ouro};">Próximo passo</div>
        <div style="font-size:18px;font-weight:800;margin-top:6px;">Conversar com ${escapar(userInfo.name ?? 'seu especialista')}</div>
        <div style="font-size:11px;margin-top:6px;color:rgba(255,255,255,0.75);">${[userInfo.phone, userInfo.email].filter(Boolean).map(escapar).join(' · ')}</div>
      </div>
      <div style="font-size:10px;color:rgba(255,255,255,0.6);text-align:right;">Gerado em<br/><b style="color:#fff;font-size:12px;">${new Date(e.geradoEm ?? Date.now()).toLocaleDateString('pt-BR')}</b></div>
    </div>`;
};

export type SecaoId = 'financiamento' | 'empate' | 'venda' | 'patrimonio' | 'parcelas' | 'premissas';

type Secao = { id: SecaoId; titulo: string; sub: string; html: (num: string) => string };

/** Seções que este estudo tem dados para mostrar, na ordem das páginas (a capa vem sempre antes). */
const secoesDoEstudo = (e: Estudo, userInfo: UserInfo, modo: ModoPaginas = 'pdf'): Secao[] => {
  const s: Secao[] = [
    { id: 'financiamento', titulo: 'O custo do dinheiro', sub: 'Consórcio, SAC e Price lado a lado', html: (n) => paginaFinanciamento(e, n, modo) },
  ];
  if (e.empate) {
    s.push({ id: 'empate', titulo: 'Onde o consórcio ganha', sub: 'Prazo, taxa e juros de empate', html: (n) => paginaEmpate(e, n, modo) });
  }
  s.push({ id: 'venda', titulo: 'Consórcio como investimento', sub: 'Vender a carta × aplicar no CDB', html: (n) => paginaVenda(e, n, modo) });
  if (e.patrimonio && e.resumo) {
    s.push({
      id: 'patrimonio',
      titulo: 'Depois da contemplação',
      sub: e.patrimonio.imovel ? 'Imóvel para alugar ou crédito aplicado' : 'O crédito aplicado pagando as parcelas',
      html: (n) => paginaPatrimonio(e, n, modo),
    });
  }
  if (e.parcelas && e.resumo) {
    s.push({ id: 'parcelas', titulo: 'As parcelas', sub: 'Ano a ano, em cada forma de contemplação', html: (n) => paginaParcelas(e, n, modo) });
  }
  s.push({ id: 'premissas', titulo: 'Premissas', sub: 'Índices oficiais e fontes', html: (n) => paginaPremissas(e, userInfo, n, modo) });
  return s;
};

/** Seções disponíveis para o vendedor ligar e desligar na pré-visualização. */
export const secoesDisponiveis = (e: Estudo): Array<{ id: SecaoId; titulo: string }> =>
  secoesDoEstudo(e, {}).map(({ id, titulo }) => ({ id, titulo }));

/**
 * HTML de cada página, na ordem: a capa e as seções que o estudo tem, menos as
 * que o vendedor desligou. A numeração e o índice da capa seguem o que ficou.
 */
export const montarPaginas = (
  e: Estudo,
  userInfo: UserInfo,
  origin: string,
  modo: ModoPaginas = 'pdf',
  ocultas: readonly SecaoId[] = Array.isArray(e.opcoes.secoesOcultas) ? e.opcoes.secoesOcultas : [],
): string[] => {
  const secoes = secoesDoEstudo(e, userInfo, modo).filter((s) => !ocultas.includes(s.id));
  const conteudos: Array<{ html: string; escura?: boolean }> = [
    { html: capa(e, userInfo, origin, modo), escura: true },
    ...secoes.map((s, i) => ({ html: s.html(String(i + 1).padStart(2, '0')) })),
  ];
  return conteudos.map((c, i) =>
    pagina({ conteudo: c.html, numero: i + 1, total: conteudos.length, e, origin, escura: c.escura, modo }),
  );
};
